/**
 * marchingSquares — hand-rolled iso-contour extraction over a regular scalar
 * grid (house style: point-in-polygon, shoelace, and Horn gradients are
 * likewise hand-rolled in this codebase; no charting/GIS dependency).
 *
 * Contract: `marchingSquaresRings(grid, level)` returns CLOSED rings (first
 * point repeated last) in local metres tracing `value == level`; grid nodes
 * with `value >= level` lie inside a ring. Regions that reach the grid edge
 * are closed along the domain boundary (the grid is padded with a virtual
 * ring of `PAD_VALUE` so every region closes; interpolated crossings are
 * clamped back onto the real domain).
 *
 * Determinism: fixed row-major cell scan, creation-order segment chaining,
 * saddle cells disambiguated by the cell-centre average — identical inputs
 * yield byte-identical output (frozen-snapshot requirement).
 *
 * NaN nodes are treated as `PAD_VALUE` (far below any contour level), i.e.
 * "no data" reads as outside every band.
 */

export interface ScalarGrid {
  columns: number;
  rows: number;
  spacingM: number;
  /** Row-major node values; node (c, r) sits at local (c·spacingM, r·spacingM). */
  values: Float64Array;
}

export interface LocalRingPoint {
  xM: number;
  yM: number;
}

/** Virtual out-of-domain value — far below any physically meaningful dBA. */
const PAD_VALUE = -1e12;

/** Edge of the padded cell lattice a contour crossing sits on. */
type EdgeKey = string;

interface Segment {
  a: EdgeKey;
  b: EdgeKey;
}

export function marchingSquaresRings(
  grid: ScalarGrid,
  level: number,
): LocalRingPoint[][] {
  const { columns, rows, spacingM } = grid;
  if (!Number.isFinite(level)) throw new RangeError(`contour level must be finite; received ${level}`);
  if (!Number.isInteger(columns) || !Number.isInteger(rows) || columns < 2 || rows < 2) {
    throw new RangeError(`grid must be at least 2x2; received ${columns}x${rows}`);
  }
  if (!Number.isFinite(spacingM) || spacingM <= 0) {
    throw new RangeError(`grid spacing must be positive and finite; received ${spacingM}`);
  }
  if (grid.values.length !== columns * rows) {
    throw new RangeError(`values length ${grid.values.length} != columns*rows ${columns * rows}`);
  }

  // Node accessor over the PADDED index space c,r in [-1, columns] x [-1, rows].
  const valueAt = (c: number, r: number): number => {
    if (c < 0 || r < 0 || c >= columns || r >= rows) return PAD_VALUE;
    const v = grid.values[r * columns + c];
    return Number.isFinite(v) ? v : PAD_VALUE;
  };

  const maxX = (columns - 1) * spacingM;
  const maxY = (rows - 1) * spacingM;
  const clampX = (x: number): number => (x < 0 ? 0 : x > maxX ? maxX : x);
  const clampY = (y: number): number => (y < 0 ? 0 : y > maxY ? maxY : y);

  // Crossing point on the edge from padded node (c1,r1) to (c2,r2), linearly
  // interpolated, then clamped onto the real domain (pad crossings land just
  // outside; clamping closes boundary rings along the domain edge).
  const crossingPoint = (c1: number, r1: number, c2: number, r2: number): LocalRingPoint => {
    const v1 = valueAt(c1, r1);
    const v2 = valueAt(c2, r2);
    let t = (level - v1) / (v2 - v1);
    if (!Number.isFinite(t)) t = 0.5;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    return {
      xM: clampX((c1 + (c2 - c1) * t) * spacingM),
      yM: clampY((r1 + (r2 - r1) * t) * spacingM),
    };
  };

  // Edge identity in the padded lattice. Horizontal edge h:(c,r) joins nodes
  // (c,r)-(c+1,r); vertical edge v:(c,r) joins (c,r)-(c,r+1).
  const hKey = (c: number, r: number): EdgeKey => `h:${c}:${r}`;
  const vKey = (c: number, r: number): EdgeKey => `v:${c}:${r}`;

  const segments: Segment[] = [];
  const pointByEdge = new Map<EdgeKey, LocalRingPoint>();

  const notePoint = (key: EdgeKey, c1: number, r1: number, c2: number, r2: number): void => {
    if (!pointByEdge.has(key)) pointByEdge.set(key, crossingPoint(c1, r1, c2, r2));
  };

  // Scan every cell of the padded lattice: top-left node (c,r), c,r in [-1, columns-1]/[−1, rows−1].
  for (let r = -1; r < rows; r++) {
    for (let c = -1; c < columns; c++) {
      const tl = valueAt(c, r) >= level ? 8 : 0;
      const tr = valueAt(c + 1, r) >= level ? 4 : 0;
      const br = valueAt(c + 1, r + 1) >= level ? 2 : 0;
      const bl = valueAt(c, r + 1) >= level ? 1 : 0;
      const caseIndex = tl | tr | br | bl;
      if (caseIndex === 0 || caseIndex === 15) continue;

      const top = hKey(c, r);
      const bottom = hKey(c, r + 1);
      const left = vKey(c, r);
      const right = vKey(c + 1, r);

      const emit = (a: EdgeKey, b: EdgeKey): void => {
        segments.push({ a, b });
      };
      const noteTop = () => notePoint(top, c, r, c + 1, r);
      const noteBottom = () => notePoint(bottom, c, r + 1, c + 1, r + 1);
      const noteLeft = () => notePoint(left, c, r, c, r + 1);
      const noteRight = () => notePoint(right, c + 1, r, c + 1, r + 1);

      switch (caseIndex) {
        case 1: case 14: noteLeft(); noteBottom(); emit(left, bottom); break;
        case 2: case 13: noteBottom(); noteRight(); emit(bottom, right); break;
        case 3: case 12: noteLeft(); noteRight(); emit(left, right); break;
        case 4: case 11: noteTop(); noteRight(); emit(top, right); break;
        case 6: case 9: noteTop(); noteBottom(); emit(top, bottom); break;
        case 7: case 8: noteTop(); noteLeft(); emit(top, left); break;
        case 5: {
          // Saddle (TR+BL inside): disambiguate by cell-centre average.
          const centre = (valueAt(c, r) + valueAt(c + 1, r) + valueAt(c, r + 1) + valueAt(c + 1, r + 1)) / 4;
          noteTop(); noteBottom(); noteLeft(); noteRight();
          if (centre >= level) {
            emit(top, left); emit(bottom, right);
          } else {
            emit(top, right); emit(left, bottom);
          }
          break;
        }
        case 10: {
          // Saddle (TL+BR inside).
          const centre = (valueAt(c, r) + valueAt(c + 1, r) + valueAt(c, r + 1) + valueAt(c + 1, r + 1)) / 4;
          noteTop(); noteBottom(); noteLeft(); noteRight();
          if (centre >= level) {
            emit(top, right); emit(left, bottom);
          } else {
            emit(top, left); emit(bottom, right);
          }
          break;
        }
        default: break;
      }
    }
  }

  // Chain segments into closed rings. Each edge key appears in exactly two
  // segments of a closed contour; walk in creation order for determinism.
  const segmentsByEdge = new Map<EdgeKey, number[]>();
  segments.forEach((segment, index) => {
    for (const key of [segment.a, segment.b]) {
      const list = segmentsByEdge.get(key);
      if (list) list.push(index);
      else segmentsByEdge.set(key, [index]);
    }
  });

  const used = new Array<boolean>(segments.length).fill(false);
  const rings: LocalRingPoint[][] = [];

  for (let start = 0; start < segments.length; start++) {
    if (used[start]) continue;
    const ringKeys: EdgeKey[] = [];
    let currentIndex = start;
    let entryKey = segments[start].a;
    // Walk: enter each segment on one edge, exit on the other, hop to the
    // partner segment sharing the exit edge.
    for (;;) {
      used[currentIndex] = true;
      const segment = segments[currentIndex];
      const exitKey = segment.a === entryKey ? segment.b : segment.a;
      ringKeys.push(exitKey);
      const partners = segmentsByEdge.get(exitKey) ?? [];
      const nextIndex = partners.find((index) => !used[index]);
      if (nextIndex === undefined) break;
      currentIndex = nextIndex;
      entryKey = exitKey;
    }
    if (ringKeys.length < 3) continue;
    const ring = ringKeys.map((key) => {
      const point = pointByEdge.get(key);
      /* istanbul ignore next -- every emitted edge key has a noted point */
      if (!point) throw new Error(`marchingSquares: missing crossing for edge ${key}`);
      return { xM: point.xM, yM: point.yM };
    });
    ring.push({ ...ring[0] });
    rings.push(ring);
  }

  return rings;
}
